import { useProduct } from '../../context/ProductContext';
import irBadge from '../../assets/ir-badge.png';
import type { ProductId } from '../../lib/api';

interface BossMarkProps {
  scale?: number;
  collapsed?: boolean;
  centered?: boolean;
  /** Optional explicit identity for previews that show a non-active product. */
  product?: ProductId;
  productName?: string;
}

/**
 * Neutral product lockup. Names and the active product come from ProductProvider.
 */
export function BossMark({
  scale = 1,
  collapsed = false,
  centered = false,
  product,
  productName: productNameOverride,
}: BossMarkProps) {
  const { activeProduct: contextProduct, productName: contextProductName } = useProduct();
  const activeProduct = product ?? contextProduct;
  const acronym = activeProduct.toUpperCase();
  const productName = (productNameOverride ?? (product ? acronym : contextProductName)).trim();
  const name = productName.toUpperCase() === acronym ? '' : productName;
  const centeredLockup = centered && !collapsed;
  return (
    <div
      className={centeredLockup ? 'relative flex w-full items-center justify-center' : 'flex items-center'}
      style={centeredLockup ? undefined : { gap: 10 * scale }}
    >
      <img
        src={irBadge}
        alt="IR"
        className={`flex-shrink-0 object-contain${centeredLockup ? ' absolute left-0 top-1/2 -translate-y-1/2' : ''}`}
        style={{ width: 28 * scale, height: 28 * scale }}
      />
      {!collapsed && (
        <div className={centeredLockup ? 'leading-none text-center' : 'leading-none'}>
          <div className="font-bold text-text-primary" style={{ fontSize: 15 * scale, letterSpacing: '0.18em' }}>
            {acronym}
            {name ? (
              <span className="font-semibold text-text-secondary" style={{ letterSpacing: '0.03em' }}>
                {' '} - {name}
              </span>
            ) : null}
          </div>
          <div
            className="vs-mono mt-1 text-text-muted"
            style={{ fontSize: 8.5 * scale, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}
          >
            {activeProduct === 'hos' ? 'HOME OPERATING SYSTEM' : 'BUSINESS OPERATING SYSTEM'}
          </div>
        </div>
      )}
    </div>
  );
}

interface BossLogoProps {
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

export function BossLogo({ collapsed = false, onCollapseToggle }: BossLogoProps) {
  return (
    <div
      className={[
        'flex items-center gap-2 w-full',
        collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-3.5',
      ].join(' ')}
    >
      <div className="flex-1 min-w-0">
        <BossMark collapsed={collapsed} />
      </div>
      {onCollapseToggle && (
        <button
          type="button"
          onClick={onCollapseToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={[
            'flex-shrink-0 grid place-items-center text-text-muted hover:text-text-primary',
            'border border-border bg-surface-2/40 hover:bg-surface-2 transition-colors',
            collapsed ? 'w-6 h-6 rounded-md' : 'w-[22px] h-[22px] rounded',
          ].join(' ')}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <path
              d={collapsed ? 'M3.5 2L6.5 5L3.5 8' : 'M6.5 2L3.5 5L6.5 8'}
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
